import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { cn } from '@/internationalization/cn'
import { useT } from '@/internationalization/i18n'
import { useEscapeKey, useLockBodyScroll } from '@/hooks'
import { IconButton } from './IconButton'

export interface DrawerProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  children: ReactNode
  footer?: ReactNode
  side?: 'right' | 'left'
  width?: 'sm' | 'md' | 'lg'
  className?: string
}

const WIDTHS = { sm: 'sm:max-w-sm', md: 'sm:max-w-md', lg: 'sm:max-w-xl' } as const

/**
 * A side panel for context that accompanies the page rather than replacing it
 * — meeting filters, a participant list, the mobile navigation.
 *
 * Full width below sm. Keep the underlying page scroll position: a drawer that
 * unmounts the list behind it loses the user's place in it.
 *
 * IT IS PORTALLED TO `document.body`, AND THAT IS LOAD-BEARING RATHER THAN
 * TIDINESS. `position: fixed` resolves against the nearest ancestor carrying a
 * transform, filter or BACKDROP-FILTER -- and the top bar has `backdrop-blur`.
 * The notification bell lives in that top bar, so rendered in place this
 * panel's `inset-0` collapsed to the 55px-tall header box: the title row was
 * all you could see, every notification below it was clipped away, and the
 * unread badge went on saying "9+" over an inbox that looked empty. Nothing
 * errored, and it cannot happen for a drawer opened from the page body, which
 * is why the two callers that predate the bell always looked fine.
 */
export function Drawer({
  isOpen,
  onClose,
  title,
  children,
  footer,
  side = 'right',
  width = 'md',
  className,
}: DrawerProps) {
  const t = useT()
  useEscapeKey(onClose, isOpen)
  useLockBodyScroll(isOpen)

  if (!isOpen) return null

  return createPortal(
    <div className="fixed inset-0 z-drawer">
      <div className="absolute inset-0 animate-fade-in bg-black/45" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-label={title ?? 'Panel'}
        className={cn(
          'absolute inset-y-0 flex w-full flex-col bg-surface shadow-lg',
          'animate-slide-in-right',
          side === 'right' ? 'right-0 border-l' : 'left-0 border-r',
          WIDTHS[width],
          className,
        )}
      >
        {title && (
          <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3">
            <h2 className="text-md font-semibold text-fg">{title}</h2>
            <IconButton label={t('common.closePanel')} icon={<X />} size="sm" onClick={onClose} />
          </div>
        )}
        <div className="min-h-0 flex-1 overflow-y-auto">{children}</div>
        {footer && <div className="border-t border-border px-4 py-3">{footer}</div>}
      </div>
    </div>,
    document.body,
  )
}
